/* eslint-disable @typescript-eslint/no-unused-vars */
import * as SchemeServiceTypes from 'src/services/storage/project/scheme/types'
import * as storageObjectsState from 'src/store/modules/storage/objects/state'
import { camelCase } from 'src/utils/camelCase'
import {
  ISchema,
  CommunicationTypes,
  ISchemaChoice,
} from 'src/services/storage/project/scheme/types'
import { ISmartListUserOptions } from 'src/services/storage/project/scheme/smart-list'
import type { ISchemeRelations } from 'src/services/storage/project/scheme/relations/types'
import {
  PARENT_COLUMN_ID,
  PARENT_PRIMARY_SORT_KEY,
  isRelatedFieldCandidate,
  makeRelatedPath,
} from './tableColumns'

export const getObjectValueKey = (schema: ISchema) =>
  camelCase(schema.identifier)

export const isSchemaLinkable = (schema: ISchema) =>
  (
    [
      CommunicationTypes.SITE,
      CommunicationTypes.FACEBOOK,
      CommunicationTypes.X,
      CommunicationTypes.LINKEDIN,
    ] as string[]
  ).includes(schema.communicationType || '')

export const isSchemaClalable = (schema: ISchema) =>
  (
    [
      CommunicationTypes.PHONE,
      // CommunicationTypes.TELEGRAM,
      CommunicationTypes.VIBER,
      // CommunicationTypes.WHATSAPP,
    ] as string[]
  ).includes(schema.communicationType || '')

export const getCommunicationTypeGeneral = (schema: ISchema) => {
  if (!schema.communicationType) return 'text'
  if (schema.communicationType === 'email') return 'email'
  if (isSchemaLinkable(schema)) return 'link'
  if (isSchemaClalable(schema)) return 'phone'

  return 'text'
}
export const createSchemeFieldTypeSlug = (
  schema: SchemeServiceTypes.ISchemaProps
): string =>
  [
    schema.valueType,
    schema.isMultivalue ? 'multivalue' : '',
    schema.visualStyle,
    schema.mediaType,
    schema.communicationType,
  ]
    .filter((type) => type)
    .join('.')

interface ISetupColumnsProps {
  schemaName: string
  fieldsGroups: SchemeServiceTypes.IFieldsGroupData[]
  schemas: SchemeServiceTypes.ISchemaInfo[]
  columns?: ISmartListUserOptions['columns']
  /** `undefined` until GET /scheme/{scheme}/relations has answered, `null` when it is unavailable */
  relations?: ISchemeRelations | null
  /** Fields of the related schemes, by scheme identifier (the own scheme reuses its own fields) */
  relatedFieldsBySchemeName?: Record<string, ISchema[]>
}

export interface ISetupColumnsResult {
  columns: storageObjectsState.IColumn[]
  /** Saved entries of related columns that could not be resolved this time */
  unresolved: storageObjectsState.IUnresolvedColumn[]
}

// New related candidates are appended after every own column until the user moves them
const RELATED_COLUMNS_INDEX_OFFSET = 10000

export const setupColumns = (
  props: ISetupColumnsProps
): ISetupColumnsResult => {
  const {
    schemaName,
    fieldsGroups,
    schemas,
    columns,
    relations,
    relatedFieldsBySchemeName,
  } = props

  const baseRelation = schemas.find((scheme) =>
    scheme.children?.find?.((child) => child.identifier === schemaName)
  )

  const currentSchema = schemas.find(
    (scheme) => scheme.identifier === schemaName
  )

  const fields = fieldsGroups.reduce((previous, current) => {
    if (current.schemas) {
      previous.push(...current.schemas)
    }

    return previous
  }, [] as SchemeServiceTypes.ISchema[])

  const columnsSync: storageObjectsState.IColumn[] = fields
    .map((schema) => {
      const column = columns?.find(
        (item) => item.identifier === schema.identifier
      )

      return {
        identifier: schema.identifier,
        slug: createSchemeFieldTypeSlug(schema),
        objectValueKey: getObjectValueKey(schema),
        enable: column ? column.enable : true,
        width: column ? column.width : 200,
        priority: {
          // Note: a saved index of 0 falls back to sortIndex here (pre-existing)
          index: column?.index || schema.sortIndex,
          static: false,
        },
        schema,
        kind: 'own' as const,
        path: schema.identifier,
      }
    })
    .sort((a, b) => a.priority.index - b.priority.index)

  const tableColumns: storageObjectsState.IColumn[] = []

  const schemaColumn = columns?.find((item) => item.identifier === schemaName)

  tableColumns.push({
    identifier: schemaName,
    slug: schemaName,
    objectValueKey: schemaName,
    //@ts-ignore
    title: currentSchema?.title || schemaName,
    width: schemaColumn?.width,
    enable: true,
    priority: {
      index: -10,
      static: true,
    },
    kind: 'main',
  })

  if (baseRelation) {
    const parentObjectIdentifier = PARENT_COLUMN_ID
    const columnParentObject = columns?.find(
      (item) => item.identifier === parentObjectIdentifier
    )
    tableColumns.push({
      identifier: parentObjectIdentifier,
      slug: parentObjectIdentifier,
      objectValueKey: parentObjectIdentifier,
      enable: columnParentObject ? columnParentObject?.enable : true,
      //@ts-ignore
      title: baseRelation?.title,
      schema: {
        identifier: parentObjectIdentifier,
        isRequired: currentSchema?.isParentObjectRequired,
        valueType: 'relationship',
        title: baseRelation?.title,
        visualStyle: 'dropdown',
        relationshipObjectSchemeIdentifier: baseRelation.identifier,
      } as SchemeServiceTypes.ISchema,
      priority: {
        index: columnParentObject ? columnParentObject?.index : -9,
        static: false,
      },
      width: columnParentObject ? columnParentObject?.width : 200,
      kind: 'parent',
      path: PARENT_PRIMARY_SORT_KEY,
    })
  }

  // Related columns: fields of the parent scheme and of the schemes behind the
  // own (non-hidden) relationship fields. They are disabled by default and only
  // built once the relations are known, so a saved related column survives in
  // `unresolved` while they load.
  const ownIdentifiers = new Set(fields.map((schema) => schema.identifier))
  const emittedPaths = new Set<string>()
  const relatedColumns: storageObjectsState.IColumn[] = []

  const pushRelatedColumns = (
    kind: 'parentField' | 'relatedField',
    relation: storageObjectsState.IColumnRelation,
    targetFields: ISchema[]
  ) => {
    targetFields.filter(isRelatedFieldCandidate).forEach((field) => {
      const path = makeRelatedPath(relation, field.identifier)

      // An own field with the same identifier wins, a path is emitted once
      if (ownIdentifiers.has(path) || emittedPaths.has(path)) {
        return
      }

      emittedPaths.add(path)

      const saved = columns?.find((item) => item.identifier === path)

      relatedColumns.push({
        identifier: path,
        slug: createSchemeFieldTypeSlug(field),
        objectValueKey: getObjectValueKey(field),
        enable: saved?.enable ?? false,
        width: saved?.width ?? 200,
        priority: {
          index:
            saved?.index ??
            RELATED_COLUMNS_INDEX_OFFSET + relatedColumns.length,
          static: false,
        },
        schema: field,
        kind,
        path,
        relation,
      })
    })
  }

  if (relations) {
    // A relation pointing back at the own scheme (self-reference) uses the own fields
    const fieldsOf = (schemeIdentifier: string) =>
      schemeIdentifier === schemaName
        ? fields
        : relatedFieldsBySchemeName?.[schemeIdentifier] || []

    if (relations.parent) {
      pushRelatedColumns(
        'parentField',
        {
          kind: 'parent',
          isMultivalue: false,
          schemeIdentifier: relations.parent.identifier,
          schemeTitle: relations.parent.title,
        },
        fieldsOf(relations.parent.identifier)
      )
    }

    relations.outgoing
      .filter((relationField) => !relationField.isHidden)
      .forEach((relationField) => {
        pushRelatedColumns(
          'relatedField',
          {
            kind: 'relationship',
            fieldIdentifier: relationField.identifier,
            fieldTitle: relationField.title,
            isMultivalue: relationField.isMultivalue,
            schemeIdentifier: relationField.objectScheme.identifier,
            schemeTitle: relationField.objectScheme.title,
          },
          fieldsOf(relationField.objectScheme.identifier)
        )
      })
  }

  // Saved dotted entries that matched nothing are kept for the next save;
  // non-dotted unknown entries (deleted own fields) are dropped as before
  const unresolvedIdentifiers = new Set<string>()
  const unresolved = (columns || []).filter((item) => {
    const isUnresolved =
      item.identifier.includes('.') &&
      !emittedPaths.has(item.identifier) &&
      !ownIdentifiers.has(item.identifier) &&
      !unresolvedIdentifiers.has(item.identifier)

    if (isUnresolved) {
      unresolvedIdentifiers.add(item.identifier)
    }

    return isUnresolved
  })

  const toBeSyncColumns = [...tableColumns, ...columnsSync, ...relatedColumns]
    .sort((a, b) => a.priority.index - b.priority.index)
    .map((item, index) => ({ ...item, priority: { ...item.priority, index } }))

  return { columns: toBeSyncColumns, unresolved }
}

export interface IObjectBoardState {
  choice: ISchemaChoice
  enable: boolean
  index: number
}
export interface IObjectBoardField {
  identifier: string
  enable: boolean
  index: number
}
export interface IObjectBoard {
  identifier: string
  title: string
  stages: IObjectBoardState[]
  fields: IObjectBoardField[]
}
export interface IObjectBoardConfig {
  boards: IObjectBoard[]
}
interface ISetupBoardProps {
  schemaName: string
  fieldsGroups: SchemeServiceTypes.IFieldsGroupData[]
  schemas: SchemeServiceTypes.ISchemaInfo[]
  boardConfig?: IObjectBoardConfig
}
export const setupBoard = (props: ISetupBoardProps): IObjectBoardConfig => {
  const { schemaName, fieldsGroups, schemas, boardConfig } = props

  // const baseRelation = schemas.find((scheme) =>
  //   scheme.children?.find?.((child) => child.identifier === schemaName)
  // )
  // const currentSchema = schemas.find(
  //   (scheme) => scheme.identifier === schemaName
  // )

  const fields = fieldsGroups.reduce((previous, current) => {
    if (current.schemas) {
      previous.push(...current.schemas)
    }

    return previous
  }, [] as SchemeServiceTypes.ISchema[])

  const stagesFields = fields.filter((field) => field.visualStyle === 'stage')
  const getStage = (boardIdentifier: string, stageId: number) =>
    boardConfig?.boards
      ?.find((board) => board.identifier === boardIdentifier)
      ?.stages?.find((stage) => stage.choice.id === stageId)

  const getField = (boardIdentifier: string, fieldIdentifier: string) =>
    boardConfig?.boards
      ?.find((board) => board.identifier === boardIdentifier)
      ?.fields?.find((field) => field.identifier === fieldIdentifier)

  const board = stagesFields.reduce(
    (config, stageField) => ({
      ...config,
      boards: [
        ...config.boards,
        {
          identifier: stageField.identifier,
          title: stageField.title,
          stages:
            stageField.choices?.map((item, index) => {
              const stage = getStage(stageField.identifier, item.id)

              return {
                choice: item,
                enable: stage?.enable === undefined ? true : stage.enable,
                index: stage?.index === undefined ? item.index : stage.index,
              }
            }) || [],
          fields: fields
            .filter((item) => item.identifier !== stageField.identifier)
            .map((item) => {
              const field = getField(stageField.identifier, item.identifier)

              return {
                identifier: item.identifier,
                enable: field?.enable === undefined ? true : field.enable,
                index:
                  field?.index === undefined ? item.sortIndex : field?.index,
              }
            })
            .sort((a, b) => a.index - b.index),
        },
      ],
    }),
    { boards: [] } as IObjectBoardConfig
  )

  return board
}
